import Seo from "@/components/Seo";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Database, ArrowRight, Sparkles, Clock } from "lucide-react";
import api from "@/lib/api";
import AIProgress from "@/pages/AIProgress";
import DataItemCard from "@/components/data/DataItemCard";
import NewsletterSignup from "@/components/NewsletterSignup";
import { timeAgo } from "@/lib/time";

function EmbeddedItem({ item }) {
  // Built-in embedded charts render their own component; everything else is a card.
  if (item.embed_key === "ai_progress_eci") {
    return (
      <div id={item.anchor} className="card-surface rounded-2xl p-4 sm:p-6 scroll-mt-24" data-testid={`data-item-${item.anchor}`}>
        <div className="mb-2">
          <h3 className="font-display font-bold text-xl leading-snug">{item.title}</h3>
          {item.description && <p className="text-sm text-[#94A3B8] mt-1.5 max-w-2xl">{item.description}</p>}
        </div>
        <AIProgress embedded />
        <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between gap-3 flex-wrap">
          <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#64748B] hover:text-[#00F0FF] transition" data-testid="data-item-source">
            Source: {item.source_name}{item.source_date ? ` · ${item.source_date}` : ""}
          </a>
        </div>
      </div>
    );
  }
  return <DataItemCard item={item} />;
}

export default function AIDataHub() {
  const [cats, setCats] = useState([]);
  const [latest, setLatest] = useState([]);

  useEffect(() => {
    api.get("/data-hub").then((r) => {
      setCats(r.data.categories || []);
      setLatest(r.data.latest || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!cats.length) return;
    const hash = window.location.hash?.slice(1);
    if (hash) {
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [cats]);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-10" data-testid="ai-data-hub">
      <Seo title="AI Data & Statistics — Charts & Sourced Metrics on AI | WorldAIInfo" description="Explore charts and statistics on the state of AI — model capability over time, global rankings, workforce impact and adoption — all from cited sources." canonicalPath="/ai-progress" />
      <div className="inline-flex items-center gap-2 mb-2">
        <Database className="w-4 h-4 text-[#00F0FF]" />
        <span className="font-mono-tech text-xs uppercase text-[#00F0FF] tracking-wider">AI Data &amp; Statistics</span>
      </div>
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">AI Data &amp; Statistics</h1>
      <p className="text-[#94A3B8] mt-3 text-base max-w-2xl">
        Real, sourced data on the world of AI — every number cited and verifiable. Explore, use, and share.
      </p>

      {latest.length > 0 && (
        <section className="mt-10" data-testid="data-hub-latest">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#00F0FF]" />
            <h2 className="font-display font-bold text-lg">Latest additions</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {latest.map((item) => (
              <Link key={item.id} to={`/data/${item.id}`}
                className="group card-surface rounded-xl p-4 flex flex-col gap-2 hover:border-[#00F0FF]/40 transition-colors"
                data-testid={`latest-item-${item.anchor}`}>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono-tech uppercase text-[#00F0FF]">
                  <Clock className="w-3 h-3" /> Added {timeAgo(item.created_at)}
                </span>
                <span className="font-display font-semibold text-sm leading-snug group-hover:text-white line-clamp-2">{item.title}</span>
                <span className="text-xs text-[#64748B] inline-flex items-center gap-1 mt-auto">
                  View <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-12 space-y-16">
        {cats.map((cat) => (
          <section key={cat.key} id={`cat-${cat.key}`} className="scroll-mt-24" data-testid={`data-category-${cat.key}`}>
            <div className="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3 mb-6">
              <div>
                <h2 className="font-display font-bold text-2xl">{cat.label}</h2>
                <p className="text-sm text-[#64748B] mt-1">{cat.description}</p>
              </div>
              <span className="font-mono-tech text-xs text-[#64748B] shrink-0">{cat.items.length} item{cat.items.length === 1 ? "" : "s"}</span>
            </div>

            {cat.items.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-10 text-center" data-testid={`data-category-empty-${cat.key}`}>
                <p className="text-sm text-[#64748B]">More data coming soon in this category.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                {cat.items.map((item) => (
                  item.type === "embedded" && !item.link_url
                    ? <div key={item.id} className="lg:col-span-2"><EmbeddedItem item={item} /></div>
                    : <DataItemCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </section>
        ))}
      </div>

      <div className="mt-16">
        <NewsletterSignup variant="inline" source="ai_data_hub" testid="data-hub-newsletter" />
      </div>
    </div>
  );
}
